import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSnackbar } from '../../redux/messageSlice';
import { getHiddenEventsApi } from '../../services/api';

const DEFAULT_ERROR = 'Failed to load hidden projects';

/**
 * GET /api/v1/events/hidden — Admin-only flat review queue of projects hidden on
 * the dashboard, most recently updated first, 15 per page.
 *
 * `next_page` is null on the last page and `events` is always an array.
 */
const useHiddenProjectsActions = () => {
  const dispatch = useDispatch();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  const getHiddenProjects = useCallback(
    async ({ append = false, pageNumber = 1, organisationId, name } = {}, callback) => {
      setIsLoading(true);
      setIsError(false);
      try {
        const params = {
          page: pageNumber,
          ...(organisationId ? { organisation_id: organisationId } : {}),
          ...(name ? { name } : {}),
        };
        const response = await getHiddenEventsApi(params);
        if (response?.data?.http_code === 200) {
          const { events: rows = [], total_count = 0, next_page } = response.data.data || {};
          setEvents((prev) => (append ? [...prev, ...rows] : rows));
          setTotalCount(total_count);
          setHasNextPage(Boolean(next_page));
          setPage(pageNumber);
          setActiveFilters({ organisationId, name });
        }
        callback && callback();
      } catch (error) {
        setIsError(true);
        const message = error?.response?.data?.message || DEFAULT_ERROR;
        dispatch(setSnackbar({ flag: true, type: 'error', message }));
      } finally {
        setIsLoading(false);
        setHasLoaded(true);
      }
    },
    [dispatch]
  );

  const loadNextPage = useCallback(() => {
    if (!hasNextPage || isLoading) return;
    getHiddenProjects({ append: true, pageNumber: page + 1, ...activeFilters });
  }, [hasNextPage, isLoading, page, activeFilters, getHiddenProjects]);

  // Called after a project is shown on the dashboard again — it no longer belongs in
  // this queue, so drop the row instead of refetching the whole page.
  const removeProject = useCallback((eventId) => {
    setEvents((prev) => prev.filter((e) => e.id !== eventId));
    setTotalCount((prev) => Math.max(prev - 1, 0));
  }, []);

  return {
    getHiddenProjects,
    loadNextPage,
    removeProject,
    events,
    isLoading,
    isError,
    hasLoaded,
    totalCount,
    hasNextPage,
  };
};

export default useHiddenProjectsActions;
